import React from 'react';
import { Message } from '../../store/services/whatsappApi';

interface MessageErrorDetailsProps {
  message: Message;
  isOpen: boolean;
  onClose: () => void;
}

const MessageErrorDetails: React.FC<MessageErrorDetailsProps> = ({
  message,
  isOpen,
  onClose
}) => {
  if (!isOpen || message.status !== 'failed') {
    return null;
  }

  const formatErrorDetails = (details: any) => {
    if (!details) return 'No additional details available';

    if (typeof details === 'string') {
      return details;
    }

    if (typeof details === 'object') {
      return JSON.stringify(details, null, 2);
    }

    return String(details);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Message Delivery Error
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Error Code */}
            {message.error_code && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Error Code
                </label>
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <code className="text-red-800 font-mono text-sm">
                    {message.error_code}
                  </code>
                </div>
              </div>
            )}

            {/* Error Message */}
            {message.error_message && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Error Message
                </label>
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-800 text-sm">
                    {message.error_message}
                  </p>
                </div>
              </div>
            )}

            {/* Error Details */}
            {message.error_details && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Error Details
                </label>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <pre className="text-gray-800 text-xs whitespace-pre-wrap font-mono overflow-x-auto">
                    {formatErrorDetails(message.error_details)}
                  </pre>
                </div>
              </div>
            )}

            {/* Message Info */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Message Information</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Contact:</span> {message.contact?.name} ({message.contact?.phone_number})
                </div>
                <div>
                  <span className="font-medium">Type:</span> {message.message_type}
                </div>
                <div>
                  <span className="font-medium">Sent:</span> {new Date(message.timestamp).toLocaleString()}
                </div>
                {message.content && (
                  <div>
                    <span className="font-medium">Content:</span>
                    <div className="mt-1 p-2 bg-gray-50 rounded text-xs max-h-20 overflow-y-auto">
                      {message.content}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Common Solutions */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Common Solutions</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Check if the recipient's phone number is correct</li>
                <li>• Verify the recipient has WhatsApp installed</li>
                <li>• Ensure the recipient hasn't blocked your number</li>
                <li>• Check your WhatsApp Business account status</li>
                <li>• Review WhatsApp's messaging policies</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageErrorDetails;